import React, { useRef, useState } from "react";
import { Avatar, Card, Flex, Typography, theme, Dropdown, Button } from "antd";
import { useSwipeable } from "react-swipeable";
import {
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  MoreOutlined,
} from "@ant-design/icons";
import { useHybridResponsive } from "../hooks";

const { Text } = Typography;

// ── Constants ─────────────────────────────────────────────────────────────────

const SNAP_THRESHOLD = 40;
const ACTION_WIDTH = 68;

// ── Status config ─────────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  active: { label: "Activo", tagColor: "success", avatarBg: (t: any) => t.colorPrimaryBg, avatarColor: (t: any) => t.colorPrimary, borderColor: (t: any) => t.colorBorderSecondary, dimmed: false },
  success: { label: "Completado", tagColor: "success", avatarBg: (t: any) => t.colorSuccessBg, avatarColor: (t: any) => t.colorSuccess, borderColor: (t: any) => t.colorSuccessBorder, dimmed: false },
  warning: { label: "Por vencer", tagColor: "warning", avatarBg: (t: any) => t.colorWarningBg, avatarColor: (t: any) => t.colorWarning, borderColor: (t: any) => t.colorWarningBorder, dimmed: false },
  error: { label: "Vencido", tagColor: "error", avatarBg: (t: any) => t.colorErrorBg, avatarColor: (t: any) => t.colorError, borderColor: (t: any) => t.colorErrorBorder, dimmed: false },
  inactive: { label: "Inactivo", tagColor: "default", avatarBg: (t: any) => t.colorFillSecondary, avatarColor: (t: any) => t.colorTextSecondary, borderColor: (t: any) => t.colorBorderSecondary, dimmed: true },
};

// ── Types ─────────────────────────────────────────────────────────────────────

interface SwipeHandlers {
  onView?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
}

interface HybridCardProps {
  title: string;
  description?: string | React.ReactNode;
  subtitle?: string | React.ReactNode;
  status?: keyof typeof STATUS_CONFIG;
  icon: React.ReactNode;
  onClick?: () => void;
  extra?: React.ReactNode;
  tagStatus?: React.ReactNode;
  swipe?: SwipeHandlers;
}

// ── Swipe action button ───────────────────────────────────────────────────────

const SwipeBtn: React.FC<{
  icon: React.ReactNode;
  label: string;
  bg: string;
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
}> = ({ icon, label, bg, onClick }) => (
  <button
    onClick={onClick}
    style={{
      display: "flex", flexDirection: "column", alignItems: "center",
      justifyContent: "center", gap: 4, width: ACTION_WIDTH, height: "99%",
      border: "none", background: bg, color: "#fff", fontSize: 11,
      fontWeight: 700, cursor: "pointer", padding: 0, flexShrink: 0,
    }}
  >
    <span style={{ fontSize: 18 }}>{icon}</span>
    {label}
  </button>
);

// ── Desktop dropdown menu ─────────────────────────────────────────────────────

const buildDropdownItems = (swipe: SwipeHandlers, token: any) => {
  const items = [];

  if (swipe.onView) {
    items.push({
      key: "view",
      icon: <EyeOutlined style={{ color: token.colorPrimary }} />,
      label: "Ver detalle",
      onClick: (info: any) => {
        info.domEvent?.stopPropagation();
        swipe.onView?.();
      },
    });
  }

  if (swipe.onEdit) {
    items.push({
      key: "edit",
      icon: <EditOutlined style={{ color: token.colorWarning }} />,
      label: "Editar",
      onClick: (info: any) => {
        info.domEvent?.stopPropagation();
        swipe.onEdit?.();
      },
    });
  }

  if ((swipe.onView || swipe.onEdit) && swipe.onDelete) {
    items.push({ type: "divider" as const });
  }

  if (swipe.onDelete) {
    items.push({
      key: "delete",
      icon: <DeleteOutlined />,
      label: "Eliminar",
      danger: true,
      onClick: (info: any) => {
        info.domEvent?.stopPropagation();
        swipe.onDelete?.();
      },
    });
  }

  return items;
};

// ── Card inner ────────────────────────────────────────────────────────────────

const CardInner: React.FC<{
  title: string;
  description?: string | React.ReactNode;
  subtitle?: string | React.ReactNode;
  icon: React.ReactNode;
  cfg: (typeof STATUS_CONFIG)[keyof typeof STATUS_CONFIG];
  extra?: React.ReactNode;
  tagStatus?: React.ReactNode;
  token: any;
  dropdownMenu?: React.ReactNode;
  isMobile: boolean
}> = ({ title, description, subtitle, icon, cfg, extra, tagStatus, token, dropdownMenu, isMobile }) => (
  <Flex align="center" justify="space-between" gap={16} wrap>
    {/* Left: avatar + text */}
    <Flex align="center" gap={16} style={{ flex: 1, minWidth: '150px' }} >
      <Avatar
        size={52}
        icon={icon}
        style={{
          backgroundColor: cfg.avatarBg(token),
          color: cfg.avatarColor(token),
          fontSize: 20,
          borderRadius: token.borderRadiusLG,
          flexShrink: 0,
        }}
      />
      <Flex vertical gap={3} style={{ minWidth: 0 }}>
        <Text strong style={{ fontSize: 14, color: token.colorText, display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {title}
        </Text>
        {subtitle && (
          <Text type="secondary" style={{ fontSize: 11, display: "block" }}>
            {subtitle}
          </Text>
        )}
        {description && (
          <Text type="secondary" style={{ fontSize: 12, display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {description}
          </Text>
        )}

      </Flex>
    </Flex>

    {/* Right: tag + extra + optional desktop dropdown */}
    <Flex align={'center'} gap={8} style={{ flexShrink: 0, width: '120px' }}>
      {tagStatus}
      {extra}
      {dropdownMenu}
    </Flex>
  </Flex>
);

// ── Main component ────────────────────────────────────────────────────────────

const HybridCard: React.FC<HybridCardProps> = ({
  title, description, subtitle, icon,
  status, onClick, extra, tagStatus, swipe,
}) => {
  const { token } = theme.useToken();
  const { isMobile } = useHybridResponsive();
  const cfg = STATUS_CONFIG[status ?? "active"];

  // ── Swipe state (mobile only) ─────────────────────────────────────────────
  const [offsetX, setOffsetX] = useState(0);
  const startX = useRef(0);
  const isDragging = useRef(false);

  const rightActions = [
    swipe?.onEdit && { icon: <EditOutlined />, label: "Editar", bg: token.colorWarning, handler: swipe.onEdit },
    swipe?.onDelete && { icon: <DeleteOutlined />, label: "Eliminar", bg: token.colorError, handler: swipe.onDelete },
  ].filter(Boolean) as { icon: React.ReactNode; label: string; bg: string; handler: () => void }[];

  const leftActions = [
    swipe?.onView && { icon: <EyeOutlined />, label: "Ver", bg: token.colorPrimary, handler: swipe.onView },
  ].filter(Boolean) as { icon: React.ReactNode; label: string; bg: string; handler: () => void }[];

  const maxRight = rightActions.length * ACTION_WIDTH;
  const maxLeft = leftActions.length * ACTION_WIDTH;

  const snapTo = (target: number) => { setOffsetX(target); isDragging.current = false; };
  const close = () => snapTo(0);

  const swipeHandlers = useSwipeable({
    onSwipeStart: () => { startX.current = offsetX; isDragging.current = true; },
    onSwiping: ({ deltaX }) => {
      if (!isDragging.current) return;
      const clamped = Math.max(-maxRight, Math.min(maxLeft, startX.current + deltaX));
      setOffsetX(clamped);
    },
    onSwiped: ({ deltaX }) => {
      const total = startX.current + deltaX;
      if (total < -SNAP_THRESHOLD && maxRight > 0) snapTo(-maxRight);
      else if (total > SNAP_THRESHOLD && maxLeft > 0) snapTo(maxLeft);
      else snapTo(0);
    },
    trackMouse: false,
    trackTouch: true,
    preventScrollOnSwipe: true,
  });

  // ── Desktop: plain card + dropdown ───────────────────────────────────────

  if (!isMobile) {
    const dropdownMenu = swipe ? (
      <Dropdown
        menu={{ items: buildDropdownItems(swipe, token) }}
        trigger={["click"]}
        placement="bottomRight"
      >
        <Button
          type="text"
          shape="circle"
          icon={<MoreOutlined style={{ fontSize: 16 }} />}
          onClick={(e) => e.stopPropagation()}
          style={{ color: token.colorTextSecondary }}
        />
      </Dropdown>
    ) : null;

    return (
      <Card
        hoverable={!cfg.dimmed}
        onClick={onClick}
        style={{
          borderRadius: token.borderRadiusLG,
          marginBottom: token.marginSM,
          borderColor: cfg.borderColor(token),
          opacity: cfg.dimmed ? 0.65 : 1,
          filter: cfg.dimmed ? "grayscale(0.4)" : "none",
          transition: "all 0.2s ease",
        }}
        styles={{ body: { padding: "16px 20px" } }}
      >
        <CardInner
          title={title} description={description} subtitle={subtitle}
          icon={icon} cfg={cfg} extra={extra} tagStatus={tagStatus}
          token={token} dropdownMenu={dropdownMenu} isMobile={isMobile}
        />
      </Card>
    );
  }

  // ── Mobile: no swipe config → plain card ─────────────────────────────────

  if (!swipe) {
    return (
      <Card
        hoverable={!cfg.dimmed}
        onClick={onClick}
        style={{
          borderRadius: token.borderRadiusLG,
          marginBottom: token.marginSM,
          borderColor: cfg.borderColor(token),
          opacity: cfg.dimmed ? 0.65 : 1,
          filter: cfg.dimmed ? "grayscale(0.4)" : "none",
          transition: "all 0.2s ease",
        }}
        styles={{ body: { padding: "16px 20px" } }}
      >
        <CardInner
          title={title} description={description} subtitle={subtitle}
          icon={icon} cfg={cfg} extra={extra} tagStatus={tagStatus} token={token} isMobile={isMobile}
        />
      </Card>
    );
  }

  // ── Mobile: swipeable card ────────────────────────────────────────────────

  return (
    <div style={{ position: "relative", overflow: "hidden", borderRadius: token.borderRadiusLG, marginBottom: token.marginSM }}>
      {/* Left actions */}
      {leftActions.length > 0 && (
        <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, display: "flex", width: maxLeft }}>
          {leftActions.map((a) => (
            <SwipeBtn key={a.label} icon={a.icon} label={a.label} bg={a.bg}
              onClick={(e) => { e.stopPropagation(); a.handler(); close(); }}
            />
          ))}
        </div>
      )}

      {/* Right actions */}
      {rightActions.length > 0 && (
        <div style={{ position: "absolute", right: 0, top: 0, bottom: 0, display: "flex", width: maxRight }}>
          {rightActions.map((a) => (
            <SwipeBtn key={a.label} icon={a.icon} label={a.label} bg={a.bg}
              onClick={(e) => { e.stopPropagation(); a.handler(); close(); }}
            />
          ))}
        </div>
      )}

      {/* Sliding card */}
      <div
        {...swipeHandlers}
        style={{
          transform: `translateX(${offsetX}px)`,
          transition: isDragging.current ? "none" : "transform 0.25s ease",
          willChange: "transform",
          position: "relative",
          zIndex: 1,
        }}
      >
        <Card
          hoverable={!cfg.dimmed}
          onClick={offsetX === 0 ? onClick : close}
          style={{
            borderRadius: token.borderRadiusLG,
            borderColor: cfg.borderColor(token),
            opacity: cfg.dimmed ? 0.65 : 1,
            filter: cfg.dimmed ? "grayscale(0.4)" : "none",
          }}
          styles={{ body: { padding: "16px 20px" } }}
        >
          <CardInner
            title={title} description={description} subtitle={subtitle}
            icon={icon} cfg={cfg} extra={extra} tagStatus={tagStatus} token={token} isMobile={isMobile}
          />
        </Card>
      </div>
    </div>
  );
};

export default HybridCard;
export type { HybridCardProps, SwipeHandlers };